import { action, observable } from 'mobx';
import { Container, Inject } from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import {
    HubConfigurationSettingsType,
    IEventHubsDAO,
    IHubFooterDTO,
} from '../../../../../../../api/event-hubs.dao';
import { EventHubsDAO } from '../../../../../../../api/implementations/event-hubs.dao';
import { IReactIntlProvider } from '../../../../../../../infrastructure/react-intl-provider';
import { IErrorParsing } from '../../../../../../../infrastructure/error-parsing';
import { alertServerError } from '../../../../../../global/modal/alerts-host/alert-templates';
import { LONG_RICH_TEXT_LENGTH } from '../../../../../../../constants/common.constants';
import { MAX_LONG_RICH_TEXT_LENGTH_ERROR_MESSAGE } from '../../../../../../../constants/validation-errors';
import { calcByteCodeSize } from '../../../../../../../infrastructure/utils/common.utils';
import {
    transpileClassesToInlineStyles,
    transpileInlineStylesToClasses,
} from '../../../../../../global/rich-text-editor/utils/transpiler';
import { HubPreviewRefreshCallback } from '../../../../_content/_hub-embed/hub-preview-sync.types';
import { buildFooterPreviewExpectation } from '../../../../_content/_hub-embed/hub-preview-sync.expectations';

const isRichTextEmpty = (value: string): boolean =>
    !value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

export class HubFooterSettingsVM {
    @observable
    private footerText = '';
    @observable
    private showWebsiteFooterLogoImage = true;
    @observable
    private loading = false;
    @observable
    private saving = false;

    private footerTextSnapshot = '';
    private showWebsiteFooterLogoImageSnapshot = true;

    constructor(
        private readonly accountId: number,
        private readonly eventHubId: number,
        @Inject private readonly eventHubsDAO: IEventHubsDAO = Container.get(EventHubsDAO),
        @Inject private readonly intlProvider: IReactIntlProvider = Container.get(IReactIntlProvider),
        private readonly onHubPreviewRefresh?: HubPreviewRefreshCallback,
    ) {
        void this.loadSettings();
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public isLoading(): boolean {
        return this.loading;
    }

    public isSaving(): boolean {
        return this.saving;
    }

    public getFooterText(): string {
        return this.footerText;
    }

    public isPoweredByLogoDisplayed(): boolean {
        return this.showWebsiteFooterLogoImage;
    }

    public getFooterTextError(): string | null {
        if (calcByteCodeSize(this.footerText) > LONG_RICH_TEXT_LENGTH) {
            return MAX_LONG_RICH_TEXT_LENGTH_ERROR_MESSAGE;
        }
        return null;
    }

    public hasUnsavedChanges(): boolean {
        return this.footerTextSnapshot !== this.footerText
            || this.showWebsiteFooterLogoImageSnapshot !== this.showWebsiteFooterLogoImage;
    }

    public isSaveButtonDisabled(): boolean {
        return this.loading || this.saving;
    }

    public isCancelButtonDisabled(): boolean {
        return this.loading || this.saving || !this.hasUnsavedChanges();
    }

    @action
    public setFooterText = (value: string): void => {
        this.footerText = value;
    };

    @action
    public togglePoweredByLogo = (): void => {
        this.showWebsiteFooterLogoImage = !this.showWebsiteFooterLogoImage;
    };

    @action
    public onCancel = (): void => {
        this.footerText = this.footerTextSnapshot;
        this.showWebsiteFooterLogoImage = this.showWebsiteFooterLogoImageSnapshot;
    };

    public onSaveChanges = (): void => {
        void this.saveSettings();
    };

    @action
    private setLoading = (loading: boolean): void => {
        this.loading = loading;
    };

    @action
    private setSaving = (saving: boolean): void => {
        this.saving = saving;
    };

    @action
    private applyLoadedState = (footerSettings: IHubFooterDTO | undefined): void => {
        const footerText = transpileInlineStylesToClasses(footerSettings?.footerText ?? '');
        const showWebsiteFooterLogoImage = footerSettings?.showWebsiteFooterLogoImage ?? true;

        this.footerText = footerText;
        this.showWebsiteFooterLogoImage = showWebsiteFooterLogoImage;

        this.footerTextSnapshot = footerText;
        this.showWebsiteFooterLogoImageSnapshot = showWebsiteFooterLogoImage;
    };

    @action
    private commitSavedState = (): void => {
        this.footerTextSnapshot = this.footerText;
        this.showWebsiteFooterLogoImageSnapshot = this.showWebsiteFooterLogoImage;
    };

    private async loadSettings(): Promise<void> {
        this.setLoading(true);
        try {
            const configs = await this.eventHubsDAO.getEventHubConfigs(this.accountId, this.eventHubId);
            const footerSettings = configs.find(
                config => config.type === HubConfigurationSettingsType.FOOTER
            ) as IHubFooterDTO | undefined;

            this.applyLoadedState(footerSettings);
        } catch (error) {
            this.handleHubApiException(error);
        } finally {
            this.setLoading(false);
        }
    }

    private async saveSettings(): Promise<void> {
        if (this.loading || this.saving || !this.hasUnsavedChanges() || this.getFooterTextError()) {
            return;
        }

        this.setSaving(true);
        const refreshGeneration = this.onHubPreviewRefresh?.beginLoading();
        try {
            const footerPayload: IHubFooterDTO = {
                accountId: this.accountId,
                eventHubId: this.eventHubId,
                type: HubConfigurationSettingsType.FOOTER,
                footerText: isRichTextEmpty(this.footerText)
                    ? null
                    : transpileClassesToInlineStyles(this.footerText),
                showWebsiteFooterLogoImage: this.showWebsiteFooterLogoImage,
            };

            await this.eventHubsDAO.setEventHubConfig(footerPayload);
            this.commitSavedState();
            void this.onHubPreviewRefresh?.refresh(
                buildFooterPreviewExpectation(footerPayload),
                refreshGeneration,
            );
        } catch (error) {
            if (refreshGeneration != null) {
                this.onHubPreviewRefresh?.cancelLoading(refreshGeneration);
            }
            this.handleHubApiException(error);
            await this.loadSettings();
        } finally {
            this.setSaving(false);
        }
    }

    private handleHubApiException(error: Error): void {
        const errorParsingService = Container.get(IErrorParsing);
        const apiError = errorParsingService.extractAPIError(error);
        const title = apiError?.errorCode === 403 ? 'Access Denied' : 'Error';
        alertServerError(title, errorParsingService.extractMessage(error));
    }
}
